import { useCallback, useEffect, useRef } from 'react';
import { confirmable, ConfirmDialog, createConfirmation } from 'react-confirm';
import styled from 'styled-components';
import { DateString } from '../../../../../common/src/date_string.ts';
import { ClientOrg, SongListType } from '../../../../../common/src/model.ts';
import * as server_api from '../../../../../common/src/server_api.ts';
import DownloadIcon from '../../../assets/download.svg?react';
import { Button } from '../../onboarding/button.tsx';

const DownloadConfirmation: ConfirmDialog<
  {
    org: ClientOrg;
    date: DateString;
    type?: SongListType;
  },
  object
> = ({ org, date, type, proceed, show }) => {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    if (show) {
      dialogRef.current?.showModal();
    } else {
      dialogRef.current?.close();
    }
  }, [show, dialogRef]);

  const onClose = useCallback(() => {
    proceed({});
  }, [proceed]);

  const noClick = useCallback(() => {}, []);

  return (
    <Dialog ref={dialogRef}>
      <DialogContent>
        <Title>Download Song Sheets</Title>
        <PermissionsMessage>
          Attention: By downloading music you are indicating that you have the right to distribute this music within
          your church.
        </PermissionsMessage>
        <DownloadPDFAnchor
          href={server_api.getPdfUrlForSongList(org.id, date, type)}
          target="_blank"
          download="song-sheets.pdf"
          rel="noreferrer"
          onClick={onClose}
        >
          <AnchorButtonWrapper onClick={noClick}>Download</AnchorButtonWrapper>
        </DownloadPDFAnchor>
        <Button notPrimary={true} onClick={onClose}>
          Cancel
        </Button>
      </DialogContent>
    </Dialog>
  );
};

const openDownloadConfirmation = createConfirmation(confirmable(DownloadConfirmation));

export function DownloadSongSheetsButton({
  org,
  date,
  type,
}: {
  org: ClientOrg;
  date: DateString;
  type?: SongListType;
}) {
  const onClick = useCallback(async () => {
    await openDownloadConfirmation({ org, date, type });
  }, [org, date, type]);

  return (
    <DownloadButton onClick={onClick}>
      <DownloadIconWrapper />
    </DownloadButton>
  );
}

const DownloadButton = styled.button`
  border: none;
`;

const DownloadIconWrapper = styled(DownloadIcon)`
  width: 28px;
  height: 28px;
  border-radius: 5px;
  padding: 3px;
  cursor: pointer;
  fill: var(--color-text);
  &:hover {
    fill: var(--color-background);
    background-color: var(--color-primary-button);
  }
`;

const Dialog = styled.dialog`
  border: none;
  border-radius: 20px;
  position: fixed;
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 20px;
  margin: auto;
  background: var(--color-background);
  top: 0;
`;

const DialogContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 25px;
  align-items: center;
  padding: 20px;
`;

const PermissionsMessage = styled.div`
  font-size: 1rem;
  max-width: 32ch;
  width: auto;
  padding-bottom: 20px;
`;

// The button is 90% of its container's width, like Cancel below it, and centred like Cancel
const DownloadPDFAnchor = styled.a`
  width: 100%;
  display: flex;
  justify-content: center;
`;

const AnchorButtonWrapper = styled(Button)``;

const Title = styled.div`
  font-size: 1.2em;
  color: var(--color-text-button-onboarding);
`;
